import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { AlertTriangle, MessageSquare, LogIn, History } from 'lucide-react';
import TranscriptChat from './TranscriptChat';
import { profiles, defaultProfile } from '../data/profiles';
import VideoSuggestions from '../components/VideoSuggestions';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useNavigate } from "react-router-dom"
import { getVideoTranscript, saveVideoTranscript, saveToWatchHistory, getRecentWatchHistory } from "../lib/supabase"
import { useAuth } from "../contexts/AuthContext"

// Add to the Window interface to prevent TypeScript errors
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: any;
  }
}

interface HomeProps {
  url: string;
  videoId: string | null;
  error: string;
  darkMode: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVideoChange: (url: string) => void;
}

interface VideoInfo {
  title: string;
  channelName: string;
}

const Home: React.FC<HomeProps> = ({
  url,
  videoId,
  error,
  darkMode,
  handleInputChange,
  onVideoChange,
}) => {
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [transcriptLoaded, setTranscriptLoaded] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoInfo, setVideoInfo] = useState<VideoInfo>({ title: "", channelName: "" });
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [activeProfile, setActiveProfile] = useState(defaultProfile);
  const [selectedVideo, setSelectedVideo] = useState<{ id: string; title: string } | null>(null);
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [recentHistory, setRecentHistory] = useState<any[]>([]);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const getVideoId = (input: string): string | null => {
    try {
      const url = new URL(input);
      if (url.hostname.endsWith('youtube.com')) {
        return url.searchParams.get('v');
      }
      if (url.hostname === 'youtu.be') {
        return url.pathname.slice(1);
      }
    } catch {
      if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
    }
    return null;
  };

  const fetchVideoInfo = async (videoId: string) => {
    try {
      const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Could not fetch video info');
      const data = await response.json();
      setVideoTitle(data.title);
      setVideoInfo({
        title: data.title,
        channelName: data.author_name || ''
      });
      return data.title;
    } catch (err) {
      console.error("Error fetching video info:", err);
      return null;
    }
  };

  const fetchTranscript = async () => {
    if (!url) return;

    setIsLoadingTranscript(true);
    setTranscriptLoaded(false);
    setTranscript("");
    setVideoTitle("");

    try {
      const videoId = getVideoId(url);
      if (!videoId) {
        throw new Error("Invalid YouTube URL");
      }

      // Get video info first and wait for it
      const title = await fetchVideoInfo(videoId);
      if (!title) {
        throw new Error("Could not fetch video info");
      }

      // Check if transcript exists in Supabase first
      const { data: existingTranscript, error: transcriptError } = await getVideoTranscript(videoId);
      
      if (existingTranscript) {
        console.log("Found existing transcript in Supabase");
        setTranscript(existingTranscript.transcript);
        setTranscriptLoaded(true);
        
        // Still save to watch history if user is logged in
        if (user) {
          await saveToWatchHistory(videoId);
        }
        
        setIsLoadingTranscript(false);
        return;
      }

      // Only proceed with Apify if user is authenticated
      if (!user) {
        setIsLoadingTranscript(false);
        return;
      }

      console.log("Starting Apify run with URL:", url);
      const response = await fetch(
        "https://api.apify.com/v2/acts/dB9f4B02ocpTICIEY/runs?token=" + import.meta.env.VITE_APIFY_API_TOKEN,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            startUrls: [url],
            language: "Default",
            includeTimestamps: "Yes",
          }),
        },
      );

      const run = await response.json();
      console.log("Full Apify run response:", run);

      if (run.error) {
        throw new Error(run.error.message || "Failed to start Apify run");
      }

      // Wait for the run to complete
      let status = "RUNNING";
      while (status === "RUNNING") {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const statusResponse = await fetch(
          `https://api.apify.com/v2/actor-runs/${run.data.id}?token=${import.meta.env.VITE_APIFY_API_TOKEN}`,
        );
        const statusData = await statusResponse.json();
        console.log("Full status response:", statusData);
        status = statusData.data.status;
        console.log("Current status:", status);
      }

      // Get the dataset ID first
      const datasetResponse = await fetch(
        `https://api.apify.com/v2/actor-runs/${run.data.id}/dataset?token=${import.meta.env.VITE_APIFY_API_TOKEN}`,
      );
      const datasetInfo = await datasetResponse.json();
      console.log("Dataset info:", datasetInfo);

      if (datasetInfo && datasetInfo.data && datasetInfo.data.id) {
        // Get items directly from the dataset
        const itemsResponse = await fetch(
          `https://api.apify.com/v2/datasets/${datasetInfo.data.id}/items?token=${import.meta.env.VITE_APIFY_API_TOKEN}`,
        );
        const items = await itemsResponse.json();
        console.log("Dataset items:", items);

        if (items && items.length > 0) {
          console.log("First item:", items[0]);
          if (items[0].transcript) {
            const transcriptData = items[0].transcript;
            console.log("Transcript data:", transcriptData);
            setTranscript(transcriptData);
            setTranscriptLoaded(true);

            // Save to watch history if user is logged in
            if (user) {
              const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
              try {
                // First save the transcript
                const { error: transcriptError } = await saveVideoTranscript({
                  video_id: videoId,
                  video_title: title,
                  video_url: url,
                  transcript: transcriptData,
                  thumbnail_url: thumbnailUrl,
                  channel_name: items[0].channelName
                });

                if (transcriptError) {
                  console.error('Error saving transcript:', transcriptError);
                  throw transcriptError;
                }

                // Then save to watch history
                const { error: watchHistoryError } = await saveToWatchHistory(videoId);
                if (watchHistoryError) {
                  console.error('Error saving watch history:', watchHistoryError);
                  throw watchHistoryError;
                }
              } catch (err) {
                console.error('Error saving data to Supabase:', err);
                throw err;
              }
            }
          } else {
            console.log("No transcript in first item:", items[0]);
            throw new Error("No transcript found for this video.");
          }
        } else {
          console.log("No items in dataset");
          throw new Error("No transcript found for this video.");
        }
      } else {
        console.log("No dataset found");
        throw new Error("No transcript found for this video.");
      }
    } catch (err) {
      console.error("Error fetching transcript:", err);
      setTranscriptLoaded(false);
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  // Handle transcript loading status
  const handleTranscriptStatus = (isLoaded: boolean) => {
    console.log('Transcript loaded status:', isLoaded);
    setTranscriptLoaded(isLoaded);
  };

  // Start loading transcript when shouldLoadTranscript changes
  useEffect(() => {
    if (videoId) {
      console.log('Starting transcript load');
      fetchTranscript();
    }
  }, [videoId]);

  // Create the embed URL with proper parameters
  const getEmbedUrl = () => {
    if (!videoId) return '';
    
    const params = new URLSearchParams({
      autoplay: '1',
      enablejsapi: '1',
      modestbranding: '1',
      rel: '0',
      showinfo: '0',
      fs: '1',
      playsinline: '1'
    });
    
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  };

  // Fetch video info when URL changes
  useEffect(() => {
    if (url) {
      const id = getVideoId(url);
      if (id) {
        fetchVideoInfo(id);
      }
    }
  }, [url]);

  const handleVideoSelect = (newVideoId: string, title: string) => {
    setSelectedVideo({ id: newVideoId, title });
  };

  const handleConfirmVideoSwitch = () => {
    if (selectedVideo && onVideoChange) {
      onVideoChange(`https://www.youtube.com/watch?v=${selectedVideo.id}`);
      setSelectedVideo(null);
    }
  };

  const handleChatClick = () => {
    if (transcriptLoaded && videoId) {
      navigate("/transcript-chat", {
        state: {
          videoUrl: url,
          transcript,
          videoTitle,
        },
      })
    }
  }

  useEffect(() => {
    if (user) {
      const fetchHistory = async () => {
        const { data, error } = await getRecentWatchHistory(10);
        if (!error && data) {
          setRecentHistory(data);
        }
      };
      fetchHistory();
    }
  }, [user]);

  return (
    <>
      <Helmet>
        <title>YouTube Player - Watch Videos Easily</title>
        <meta name="description" content="A simple, clean YouTube video player with dark mode and transcript chat. Watch your favorite videos with ease." />
        <meta name="keywords" content="youtube player, video player, youtube videos, dark mode, transcript chat" />
        <meta property="og:title" content="YouTube Player - Watch Videos Easily" />
        <meta property="og:description" content="A simple, clean YouTube video player with dark mode and transcript chat. Watch your favorite videos with ease." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://youtubeplayer.com" />
      </Helmet>

      <div className="w-full px-8 sm:px-6 lg:px-8">
        {/* URL Input Section */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              value={url}
              onChange={handleInputChange}
              placeholder="Enter YouTube video URL"
              className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
          {error && (
            <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center">
              <AlertTriangle size={18} className="mr-2" />
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Video Player and History */}
          <div className="lg:w-3/5 flex flex-col">
            {/* Video Player */}
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-light-card dark:bg-dark-card shadow-lg border border-black/5 dark:border-white/5">
              {videoId ? (
                <div className="relative w-full h-0 pb-[56.25%]">
                  <iframe
                    key={videoId}
                    src={getEmbedUrl()}
                    title="YouTube video player"
                    className="absolute top-0 left-0 w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    style={{ border: 'none' }}
                  ></iframe>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-light-background to-light-card dark:from-dark-background dark:to-dark-card p-6">
                  <p className="text-center text-light-subtle dark:text-dark-subtle text-lg">
                    Your video will appear here.
                    <span className="block text-sm mt-1">Just paste a YouTube URL above to begin!</span>
                  </p>
                </div>
              )}
            </div>

            {/* Video Info and Suggestions */}
            {videoId && videoTitle && (
              <>
                <div className="mt-4 flex items-start gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <img 
                    src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    alt={videoTitle}
                    className="w-32 h-20 object-cover rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                    }}
                  />
                  <div className="flex-1">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                      {videoTitle}
                    </h2>
                    {videoInfo?.channelName && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {videoInfo.channelName}
                      </p>
                    )}
                  </div>
                </div>

                <VideoSuggestions
                  videoId={videoId}
                  onVideoSelect={handleVideoSelect}
                />
              </>
            )}

            {/* Watch History - Always visible when user is logged in */}
            {user && recentHistory.length > 0 && (
              <div className={`${videoId ? 'mt-6' : 'pt-6'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <History size={18} className="text-purple-600 dark:text-purple-400" />
                    Watch History
                  </h3>
                  {recentHistory.length > 3 && (
                    <button
                      onClick={() => setShowAllHistory(!showAllHistory)}
                      className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                    >
                      {showAllHistory ? 'Show Less' : 'See More'}
                    </button>
                  )}
                </div>
                <div className="grid gap-4">
                  {(showAllHistory ? recentHistory : recentHistory.slice(0, 3)).map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                      onClick={() => {
                        if (item.video_transcripts) {
                          setSelectedVideo({
                            id: item.video_transcripts.video_id,
                            title: item.video_transcripts.video_title
                          });
                        }
                      }}
                    >
                      <img
                        src={item.video_transcripts?.thumbnail_url || `https://img.youtube.com/vi/${item.video_id}/mqdefault.jpg`}
                        alt={item.video_transcripts?.video_title || 'Video thumbnail'}
                        className="w-32 h-20 object-cover rounded-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://img.youtube.com/vi/${item.video_id}/hqdefault.jpg`;
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                          {item.video_transcripts?.video_title || 'Untitled Video'}
                        </h4>
                        {item.video_transcripts?.channel_name && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {item.video_transcripts.channel_name}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Watched {new Date(item.watched_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Column - Transcript Chat */}
          {videoId ? (
            <div 
              ref={transcriptRef}
              className="lg:w-2/5 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-[calc(100vh-16rem)] lg:h-[calc(100vh-12rem)]"
            >
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900">
                <h2 className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <MessageSquare size={18} className="text-purple-600 dark:text-purple-400" />
                  Transcript Chat
                </h2>
              </div>

              <div className="flex-1 overflow-hidden">
                {!user ? (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                    <LogIn size={48} className="text-purple-600 dark:text-purple-400 mb-4" />
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                      Sign in to use Transcript Chat
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Chat with AI about video content and get personalized insights.
                    </p>
                    <button
                      onClick={() => setIsAuthModalOpen(true)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Sign in
                    </button>
                  </div>
                ) : isLoadingTranscript ? (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-4"></div>
                    <p className="text-center">
                      Loading transcript...
                      <span className="block text-sm mt-1">This may take about 10 seconds</span>
                    </p>
                  </div>
                ) : transcript ? (
                  <div className="h-full">
                    <TranscriptChat
                      videoUrl={url}
                      initialTranscript={transcript}
                      initialVideoTitle={videoTitle}
                    />
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-gray-500 dark:text-gray-400">
                    <p className="text-center">
                      The chat will appear here.
                      <span className="block text-sm mt-1">Paste a YouTube URL to start chatting!</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="lg:w-2/5 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-[calc(100vh-16rem)] lg:h-[calc(100vh-12rem)]">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900">
                <h2 className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <MessageSquare size={18} className="text-purple-600 dark:text-purple-400" />
                  Transcript Chat
                </h2>
              </div>
              <div className="flex-1 flex items-center justify-center p-6">
                {!user ? (
                  <div className="text-center">
                    <LogIn size={48} className="text-purple-600 dark:text-purple-400 mb-4 mx-auto" />
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                      Sign in to use Transcript Chat
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Chat with AI about video content and get personalized insights.
                    </p>
                    <button
                      onClick={() => setIsAuthModalOpen(true)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Sign in
                    </button>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400">
                    The chat will appear here.
                    <span className="block text-sm mt-1">Paste a YouTube URL to start chatting!</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!selectedVideo}
        title={selectedVideo?.title || ''}
        onConfirm={handleConfirmVideoSwitch}
        onCancel={() => setSelectedVideo(null)}
      />
    </>
  );
};

export default Home;
